import { LogOut, Moon, Sun } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useClickOutside } from '../hooks/use-click-outside';
import { deriveInitials } from '../lib/derive-initials';
import { useAuth } from '../providers/auth-provider';
import { useTheme } from '../providers/ThemeProvider';

import { Avatar } from './Avatar';

export interface UserMenuProps {
  className?: string;
}

export function UserMenu({ className = '' }: UserMenuProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(menuRef, close, open);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, close]);

  if (!user) return null;

  const initials = deriveInitials(user.email);

  return (
    <div ref={menuRef} className={`relative ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        id="user-menu-trigger"
        aria-label="User menu"
        aria-controls={open ? 'user-menu-popover' : undefined}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center justify-center min-h-[44px] min-w-[44px] rounded-sd-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 hover:bg-sd-surface-elevated/50 active:scale-95 motion-reduce:transition-none ${open ? 'bg-sd-surface-elevated/60' : ''}`}
      >
        <Avatar size="sm" initials={initials} name={user.email} />
      </button>

      {open && (
        <div
          id="user-menu-popover"
          role="menu"
          aria-labelledby="user-menu-trigger"
          className="absolute right-0 top-full mt-sd-1 w-[220px] bg-sd-surface-elevated/95 backdrop-blur-md border border-sd-border/60 rounded-sd-lg shadow-sd-lg overflow-hidden z-50 sd-slide-up"
          style={{ boxShadow: 'var(--sd-shadow-lg), 0 0 40px rgba(0,0,0,0.04)' }}
        >
          <div
            className="absolute top-0 left-3 right-3 h-px bg-gradient-to-r from-transparent via-sd-warning/40 to-transparent"
            aria-hidden="true"
          />

          <div className="px-sd-3 pt-sd-3 pb-sd-2 border-b border-sd-border/40">
            <p className="text-sd-sm font-medium text-sd-text truncate leading-snug">
              {user.email}
            </p>
            <p className="text-sd-xs text-sd-text-muted capitalize leading-snug mt-px">
              {user.role}
            </p>
          </div>

          <div className="py-sd-1 px-sd-1">
            <button
              type="button"
              role="menuitem"
              className="group relative flex items-center gap-sd-2 w-full px-sd-2 py-sd-2 min-h-[44px] text-sd-sm text-sd-text-secondary rounded-sd-md transition-all duration-150 hover:bg-sd-warning/8 hover:text-sd-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-inset motion-reduce:transition-none"
              onClick={toggleTheme}
            >
              {theme === 'light' ? (
                <Moon
                  size={15}
                  className="transition-transform duration-150 group-hover:rotate-[-20deg] motion-reduce:transition-none"
                  aria-hidden="true"
                />
              ) : (
                <Sun
                  size={15}
                  className="transition-transform duration-150 group-hover:rotate-[20deg] motion-reduce:transition-none"
                  aria-hidden="true"
                />
              )}
              <span className="font-medium">{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
            </button>

            <div className="mx-sd-2 my-sd-1 h-px bg-sd-border/30" aria-hidden="true" />

            <button
              type="button"
              role="menuitem"
              className="group relative flex items-center gap-sd-2 w-full px-sd-2 py-sd-2 min-h-[44px] text-sd-sm text-sd-text-secondary rounded-sd-md transition-all duration-150 hover:bg-sd-error/8 hover:text-sd-error focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-inset motion-reduce:transition-none"
              onClick={() => {
                logout();
                close();
              }}
            >
              <LogOut
                size={15}
                className="transition-transform duration-150 group-hover:-translate-x-px motion-reduce:transition-none"
                aria-hidden="true"
              />
              <span className="font-medium">Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
